/**
 * As regras da aba Outros Bancos que não são fórmula de dinheiro.
 *
 * Papel: aqui ficam quem tem cartão de folha (`favorecidosDaFolha`), quem pode
 * entrar pelo "+ Novo colaborador" (`candidatosAColaborador`), o mês por
 * extenso das setas, a dica do salário importado, a leitura dos dias digitados
 * e a pergunta de "Fechar Folha" quando o cartão já foi zerado
 * (`faltaAoRefechar`).
 *
 * Como usar: as fórmulas de valor — os dias efetivos, os itens de ajuda de
 * custo, o total do cartão, a fotografia da folha — são do pacote
 * compartilhado, porque a API as usa também; o que mora aqui é só da tela.
 */
import {
  type Favorecido,
  type FolhaFechada,
  MESES_POR_EXTENSO,
  competenciaComBarra,
} from '@concierge/shared';

import { formatarData } from '@/shared/lib/formato';

/**
 * Os favorecidos que têm cartão de folha.
 *
 * @param favorecidos - Os favorecidos da casa.
 * @returns Os de conta apartada, em `localeCompare('pt-BR')` do nome.
 */
export function favorecidosDaFolha(favorecidos: readonly Favorecido[]): Favorecido[] {
  return favorecidos
    .filter((favorecido) => favorecido.contaApartada)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/**
 * Os favorecidos que o "+ Novo colaborador" sugere.
 *
 * @param favorecidos - Os favorecidos da casa.
 * @returns Os **sem** conta apartada, em `localeCompare('pt-BR', { sensitivity:
 *   'base' })`, que não separa o nome acentuado.
 */
export function candidatosAColaborador(favorecidos: readonly Favorecido[]): Favorecido[] {
  return favorecidos
    .filter((favorecido) => !favorecido.contaApartada)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
}

/**
 * O mês das setas de Outros Bancos.
 *
 * @param competencia - O mês aberto, em `AAAA-MM`.
 * @returns `Setembro 2026`.
 */
export function mesPorExtenso(competencia: string): string {
  return `${MESES_POR_EXTENSO[Number(competencia.slice(5, 7)) - 1] ?? ''} ${competencia.slice(0, 4)}`;
}

/**
 * A dica do "✓" do salário importado.
 *
 * @param competencia - A competência da linha, em `AAAA-MM`.
 * @param importadoEm - O dia da importação, em `AAAA-MM-DD`.
 * @returns `Importado do PDF (competência MM/AAAA) em DD/MM/AAAA`; o nome do
 *   arquivo não é guardado e por isso não aparece.
 */
export function dicaDoSalario(competencia: string, importadoEm: string): string {
  return `Importado do PDF (competência ${competenciaComBarra(competencia)}) em ${formatarData(importadoEm)}`;
}

/**
 * Lê o número de um campo de dias.
 *
 * @param texto - O texto do campo.
 * @returns O inteiro digitado; vazio ou inválido vale zero, como na origem.
 */
export function diasDoCampo(texto: string): number {
  return Number.parseInt(texto, 10) || 0;
}

/**
 * O que a folha já fechada tem e o cartão perdeu, antes de fechar de novo.
 *
 * @param fechada - A folha fechada do mês desse favorecido, quando há.
 * @param temSalario - Se o cartão tem salário maior que zero agora.
 * @param temGuia - Se o cartão tem guia DAE agora.
 * @returns "o salário", "a guia DAE" ou "o salário e a guia DAE"; `null`
 *   quando não há o que perder e "Fechar Folha" não pergunta nada.
 */
export function faltaAoRefechar(
  fechada: FolhaFechada | undefined,
  temSalario: boolean,
  temGuia: boolean,
): string | null {
  if (fechada === undefined) return null;
  const tinhaSalario = fechada.itens.some((item) => item.nome.startsWith('Salário líquido'));
  const tinhaGuia = fechada.itens.some((item) => item.nome.startsWith('Guia DAE'));
  const faltas = [
    tinhaSalario && !temSalario ? 'o salário' : null,
    tinhaGuia && !temGuia ? 'a guia DAE' : null,
  ].filter((falta) => falta !== null);
  return faltas.length === 0 ? null : faltas.join(' e ');
}
